/**
 * Two charts, hand-built in SVG to the dataviz mark specs rather than pulled
 * from a charting library: 2px lines, an area wash at ~10%, end dots with a
 * surface ring, columns no thicker than 24px with a 4px rounded data-end and a
 * square baseline, hairline gridlines, clean ticks, and a hover layer on both.
 * One y-axis each, always.
 *
 * Every chart sits next to a table of the same numbers, so nothing here is the
 * only way to read a value.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { londonDay } from '@totum-analytics/shared/dates';
import { formatCount, formatDay } from '../lib/format';

/** Today is still happening; say so rather than let a half-day look like a collapse. */
function dayLabel(day: string): string {
  return day === londonDay(new Date()) ? `${formatDay(day, true)} · so far` : formatDay(day, true);
}
import { labelIndices, linear, niceTicks } from '../lib/scale';

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(640);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => entry && setWidth(Math.max(240, entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const PAD = { top: 12, right: 16, bottom: 28, left: 44 };

function Grid({ ticks, y, width }: { ticks: number[]; y: (v: number) => number; width: number }) {
  return (
    <g>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke="var(--color-grid)" strokeWidth={1} />
          <text x={PAD.left - 8} y={y(t)} dy="0.32em" textAnchor="end" fontSize={11} fill="#6b7280" className="tabular">
            {formatCount(t, { compact: true })}
          </text>
        </g>
      ))}
    </g>
  );
}

function Tooltip({ x, width, children }: { x: number; width: number; children: ReactNode }) {
  const left = Math.min(Math.max(x - 80, 0), width - 168);
  return (
    <div
      className="pointer-events-none absolute top-0 w-40 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md"
      style={{ left }}
      role="status"
    >
      {children}
    </div>
  );
}

export interface Series {
  name: string;
  color: string;
  values: number[];
}

export function LineChart({ days, series, height = 240, label }: { days: string[]; series: Series[]; height?: number; label: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1] as number;
  const n = days.length;
  const x = linear([0, Math.max(1, n - 1)], [PAD.left, width - PAD.right]);
  const y = linear([0, top], [height - PAD.bottom, PAD.top]);
  const path = (vals: number[]) => vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
  const labels = labelIndices(n, width - PAD.left - PAD.right);

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const i = Math.round(((e.clientX - rect.left - PAD.left) / (width - PAD.left - PAD.right)) * (n - 1));
    setHover(Math.min(n - 1, Math.max(0, i)));
  };

  return (
    <div ref={ref} className="relative">
      {series.length > 1 && (
        <div className="flex gap-4 mb-2 text-xs text-gray-600" aria-hidden="true">
          {series.map((s) => (
            <span key={s.name} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 rounded" style={{ background: s.color }} />
              {s.name}
            </span>
          ))}
        </div>
      )}
      <svg width={width} height={height} role="img" aria-label={label} onPointerMove={onMove} onPointerLeave={() => setHover(null)} className="block touch-none">
        <Grid ticks={ticks} y={y} width={width} />
        {labels.map((i) => (
          <text key={i} x={x(i)} y={height - 8} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fontSize={11} fill="#6b7280">
            {formatDay(days[i] as string)}
          </text>
        ))}
        {series[0] && n > 1 && (
          <path d={`${path(series[0].values)}L${x(n - 1)},${y(0)}L${x(0)},${y(0)}Z`} fill={series[0].color} opacity={0.1} />
        )}
        {series.map((s) => (
          <path key={s.name} d={path(s.values)} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={height - PAD.bottom} stroke="#9ca3af" strokeWidth={1} />}
        {series.map((s) => {
          const i = hover ?? n - 1;
          const v = s.values[i];
          return v === undefined ? null : (
            <circle key={s.name} cx={x(i)} cy={y(v)} r={4} fill={s.color} stroke="var(--color-surface)" strokeWidth={2} />
          );
        })}
      </svg>
      {hover !== null && (
        <Tooltip x={x(hover)} width={width}>
          <p className="font-medium text-gray-900 mb-1">{dayLabel(days[hover] as string)}</p>
          {series.map((s) => (
            <p key={s.name} className="flex items-center justify-between gap-2 text-gray-700">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.color }} />
                {s.name}
              </span>
              <span className="tabular font-medium text-gray-900">{formatCount(s.values[hover])}</span>
            </p>
          ))}
        </Tooltip>
      )}
    </div>
  );
}

export function ColumnChart({ days, values, color, name, height = 200, label }: { days: string[]; values: number[]; color: string; name: string; height?: number; label: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const ticks = niceTicks(Math.max(1, ...values));
  const top = ticks[ticks.length - 1] as number;
  const n = values.length;
  const inner = width - PAD.left - PAD.right;
  const band = inner / Math.max(1, n);
  const bar = Math.min(24, Math.max(2, band - 2)); // capped at 24px, with at least a 2px surface gap
  const y = linear([0, top], [height - PAD.bottom, PAD.top]);
  const base = y(0);
  const labels = labelIndices(n, inner);
  const r = Math.min(4, bar / 2);

  return (
    <div ref={ref} className="relative">
      <svg width={width} height={height} role="img" aria-label={label} onPointerLeave={() => setHover(null)} className="block">
        <Grid ticks={ticks} y={y} width={width} />
        {values.map((v, i) => {
          const cx = PAD.left + band * i + band / 2;
          const x0 = cx - bar / 2;
          const yv = y(v);
          const h = base - yv;
          // Rounded data-end, square at the baseline.
          const d = h <= 0 ? '' : h < r
            ? `M${x0},${base}V${yv}H${x0 + bar}V${base}Z`
            : `M${x0},${base}V${yv + r}Q${x0},${yv} ${x0 + r},${yv}H${x0 + bar - r}Q${x0 + bar},${yv} ${x0 + bar},${yv + r}V${base}Z`;
          return (
            <g key={i} onPointerEnter={() => setHover(i)}>
              <rect x={PAD.left + band * i} y={PAD.top} width={band} height={height - PAD.top - PAD.bottom} fill="transparent" />
              {d && <path d={d} fill={color} opacity={hover === null || hover === i ? 1 : 0.55} />}
            </g>
          );
        })}
        {labels.map((i) => (
          <text key={i} x={PAD.left + band * i + band / 2} y={height - 8} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fontSize={11} fill="#6b7280">
            {formatDay(days[i] as string)}
          </text>
        ))}
      </svg>
      {hover !== null && (
        <Tooltip x={PAD.left + band * hover + band / 2} width={width}>
          <p className="font-medium text-gray-900">{dayLabel(days[hover] as string)}</p>
          <p className="text-gray-700">
            {name}: <span className="tabular font-medium text-gray-900">{formatCount(values[hover])}</span>
          </p>
        </Tooltip>
      )}
    </div>
  );
}

/** A proportion bar inside a table cell: the row's share of the column maximum. */
export function ShareBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="h-1.5 w-full rounded-full bg-gray-100" aria-hidden="true">
      <div className="h-1.5 rounded-full bg-series-1" style={{ width: `${pct}%` }} />
    </div>
  );
}
